<template>
  <page-header title="Onboarding Slides">
    <router-link
      to="/onboarding-slide"
      class="btn-primary ml-auto h-10"
    >
      <PlusCircleIcon class="size-5 -ml-1 mr-2" />
      Add
    </router-link>
  </page-header>

  <div v-if="isEmpty && search === null" />

  <template v-else>
    <div class="flex justify-between mb-4">
      <input-search v-model.trim="search" class="max-w-lg grow" />

      <reload-button class="ml-4" @click="reloadGrid" />
    </div>

    <grid
      ref="gridRef"
      :data-handler="getOnboardingSlides"
      :columns="columns"
      :row-class="rowClass"
      :per-page="15"
      @enable="onEnable"
      @disable="onDisable"
      @preview="onPreview"
      @delete="onDelete"
    />
  </template>

  <PreviewOnboardingSlide v-model="isModalOpen" :item="currentSlide" />
</template>

<script setup>
import { ref, markRaw } from 'vue';
import PageHeader from '../components/PageHeader.vue';
import ReloadButton from '../components/grid/ReloadButton.vue';
import InputSearch from '../components/grid/InputSearch.vue';
import Grid from '../components/grid/Grid.vue';
import OnboardingSlideListActions from '../components/OnboardingSlideListActions.vue';
import { useFetch, watchDebounced } from '@vueuse/core';
import { useRouteQuery } from '@vueuse/router';
import { PlusCircleIcon } from '@heroicons/vue/24/outline';
import { useRouter } from 'vue-router';
import { apiDomain } from '../composables/useConstants';
import { Modal } from '../composables/useModal';
import { useToast } from 'vue-toastification';
import PreviewOnboardingSlide from '../components/PreviewOnboardingSlide.vue';
import { formatDate } from '../utils/formatters';
import SlideIcon from '../components/SlideIcon.vue';

const gridRef = ref(null);
const isEmpty = ref(false);
const router = useRouter();
const toast = useToast();
const currentSlide = ref(null);
const isModalOpen = ref(false);


function rowClass(_row, index) {
  return (index % 2 ? 'bg-zinc-200/10' : '') + (!_row.isActive ? ' inactive-row' : '');
}

function reloadGrid(updateQuery = {}) {
  if (gridRef.value?.reload) {
    gridRef.value.reload(updateQuery);
  }
}

const search = useRouteQuery('search', null);

watchDebounced(
  search,
  () => {
    reloadGrid({ offset: 0 });
  },
  { debounce: 500, maxWait: 1000 }
);

function onPreview(row) {
  currentSlide.value = row;
  isModalOpen.value = true;
}

async function onEnable(row) {
  const slideTitle = row.isFinalSlide ? (row.finalSlideConfig?.title || 'Final Slide') : row.title;
  const confirm = await Modal.confirm({
    title: 'Enable Onboarding Slide',
    body: `Enable the onboarding slide "${slideTitle}"?`,
    okButton: 'Enable',
    iconColor: 'primary'
  });

  if (!confirm) {
    return;
  }

  const { data } = await useFetch(`${apiDomain}/api/onboarding/enable/${row.id}`).get().json();

  if (data.value?.success) {
    toast.success('Onboarding slide enabled successfully');
    reloadGrid();
    return;
  }

  toast.error('Error enabling onboarding slide');
}

async function onDisable(row) {
  const slideTitle = row.isFinalSlide ? (row.finalSlideConfig?.title || 'Final Slide') : row.title;
  const confirm = await Modal.confirm({
    title: 'Disable Onboarding Slide',
    body: `Disable the onboarding slide "${slideTitle}"?`,
    okButton: 'Disable',
    iconColor: 'primary'
  });

  if (!confirm) {
    return;
  }

  const { data } = await useFetch(`${apiDomain}/api/onboarding/disable/${row.id}`).get().json();

  if (data.value?.success) {
    toast.success('Onboarding slide disabled successfully');
    reloadGrid();
    return;
  }

  toast.error('Error disabling onboarding slide');
}

async function onDelete(row) {
  const slideTitle = row.isFinalSlide ? (row.finalSlideConfig?.title || 'Final Slide') : row.title;
  const confirm = await Modal.confirm({
    title: 'Delete Onboarding Slide',
    body: `Delete the onboarding slide "${slideTitle}"?`,
    okButton: 'Delete',
    iconColor: 'danger'
  });

  if (!confirm) {
    return;
  }

  const { data } = await useFetch(`${apiDomain}/api/onboarding/${row.id}`).delete().json();

  if (data.value?.success) {
    toast.success('Onboarding slide deleted successfully');
    reloadGrid();
    return;
  }

  toast.error('Error deleting onboarding slide');
}

function getUrl({ limit, offset, sort, direction }) {
  const data = { limit, offset, order: sort, direction: direction?.toLowerCase(), search: search.value ?? '' };

  const searchParams = new URLSearchParams(data);

  return `${apiDomain}/api/onboarding/?${searchParams}`;
}

async function getOnboardingSlides(args) {
  const { limit, offset, sort, direction } = args;
  const { data } = await useFetch(getUrl({ limit, offset, sort, direction })).get().json();

  isEmpty.value = false;

  return {
    total: data.value.total,
    rows: data.value.onboardingSlides
  };
}

const columns = [
  {
    title: '#',
    prop: 'id',
    class: 'cursor-pointer text-xs font-mono',
    sortable: true,
    click: row => router.push(`/onboarding-slide/${row.id}`)
  },
  {
    title: 'Image',
    prop: 'image',
    component: markRaw(SlideIcon),
    click: row => router.push(`/onboarding-slide/${row.id}`)
  },
  {
    title: 'Title',
    prop: 'title',
    class: 'cursor-pointer',
    value: row => row.isFinalSlide ? (row.finalSlideConfig?.title || 'Final Slide') : row.title,
    click: row => router.push(`/onboarding-slide/${row.id}`)
  },
  {
    title: 'Type',
    prop: 'isFinalSlide',
    class: 'cursor-pointer text-xs',
    value: row => row.isFinalSlide ? 'Final Slide' : 'Regular Slide',
    click: row => router.push(`/onboarding-slide/${row.id}`)
  },
  {
    title: 'Description',
    prop: 'description',
    class: 'cursor-pointer text-xs text-gray-600 max-w-xs truncate',
    value: row => row.isFinalSlide ? (row.finalSlideConfig?.description || row.finalSlideConfig?.shortDescription || '-') : (row.description || '-'),
    click: row => router.push(`/onboarding-slide/${row.id}`)
  },
  {
    title: 'Order',
    prop: 'order',
    class: 'cursor-pointer',
    sortable: true,
    click: row => router.push(`/onboarding-slide/${row.id}`)
  },
  {
    title: 'Created',
    prop: 'createdAt',
    class: 'cursor-pointer',
    value: row => formatDate(row.createdAt),
    sortable: true
  },
  {
    class: 'no-animation text-right max-w-max! z-10 relative',
    component: markRaw(OnboardingSlideListActions)
  }
];
</script>
